// ═══════════════════════════════════════════════════════════════════════════
// commandDispatch/dynamicCommands.ts — DYNLIST / DYNSET / DYNRESET / PROPERTY,
// extracted verbatim from App.tsx's `submitCommandText` (D5).
// ═══════════════════════════════════════════════════════════════════════════
//
// DYNLIST / DYNSET / PROPERTY additionally accept a trailing-argument form
// via `.startsWith(...)` (e.g. "DYNSET width 250") — unchanged by D5, since
// `resolvedCommandId` only resolves the bare verb (a single whole-string
// token) and these forms carry extra text on the same line.
// ═══════════════════════════════════════════════════════════════════════════

import {
  formatDynListTranscript,
  planPropertyDispatch,
  resetDynamicParameters,
  setDynamicParameter,
  tokenize,
} from "@dxf-ai-editor/command-engine";

import { getEntityById } from "../state/editorStore";
import type { DispatchContext, DispatchResult } from "./types";

/**
 * Resolves a selected Block INSERT's live entity + owning BlockDefinition —
 * the one piece of lookup logic DYNLIST/DYNSET/DYNRESET (and PROPERTY's
 * dynamic-param branch) each need before calling their own command-engine
 * helper. Extracted to a single truth (jscpd flagged the inline version
 * duplicated 3x across this file).
 *
 * Round-3 (@codex Finding 4) — entity resolution goes through
 * `getEntityById` (compact-cache O(1) lookup first, `geometryDocument`
 * walk as the fallback for the handful of entity kinds the compact cache
 * doesn't index — REVCLOUD/ARRAYRECT/VIEWPORT), NOT a manual
 * `geometryDocument.spaces[].entities` scan. A streamed/very-large import
 * intentionally leaves `space.entities` EMPTY (entities live only in the
 * compact cache — see `spaceReadThrough.ts`'s module doc); the old manual
 * scan here reported "not found" for every valid id in that case even
 * though the reducers this module dispatches into (`dynamic/set`,
 * `dynamic/list`, `dynamic/reset`) mutate the SAME compact-cache-backed
 * document just fine. `getEntityById` is the same cache-aware resolver
 * every other id-driven entity lookup in the app already uses (OSNAP,
 * grip-drag, the Properties panel — see its doc comment in
 * `state/editorStore.ts`).
 */
function resolveDynamicBlockTarget(
  geometryDocument: DispatchContext["geometryDocument"],
  targetId: string,
) {
  const entity = getEntityById(targetId);
  const blockDef =
    entity && entity.type === "Block"
      ? geometryDocument.blocks[entity.blockName]
      : undefined;
  return { entity, blockDef };
}

/**
 * DYNLIST/DYNSET/DYNRESET all require "the first Block in the current
 * selection" before doing anything else — same "find the target, resolve
 * its entity + blockDef" pair of steps 3x (jscpd flagged this too). One
 * truth: `null` means no Block is selected (caller emits the
 * feed.dyn.nothing_selected message and bails), otherwise the caller gets
 * the target id + resolved entity/blockDef in one call.
 */
function resolveSelectedDynamicTarget(
  ctx: Pick<DispatchContext, "selectedEntities" | "geometryDocument">,
) {
  const target = ctx.selectedEntities.find((e) => e.type === "Block");
  if (!target || target.type !== "Block") return null;
  const { entity, blockDef } = resolveDynamicBlockTarget(
    ctx.geometryDocument,
    target.id,
  );
  return { targetId: target.id, entity, blockDef };
}

/**
 * DYNSET/DYNRESET share the identical "resolve or bail with the
 * nothing-selected feed message" prelude (jscpd flagged the two literal
 * copies). Emits the feed message + clears the command box itself when
 * nothing is selected, so callers only need `if (!resolved) return true;`.
 */
function requireSelectedDynamicTarget(
  ctx: Pick<
    DispatchContext,
    "selectedEntities" | "geometryDocument" | "appendCommandFeed" | "t" | "setCommandText"
  >,
) {
  const resolved = resolveSelectedDynamicTarget(ctx);
  if (!resolved) {
    ctx.appendCommandFeed(ctx.t("feed.dyn.nothing_selected"));
    ctx.setCommandText("");
  }
  return resolved;
}

/**
 * DYNLIST / DYNSET / DYNRESET — INSERT customProperties CLI. One-shot
 * commands that operate on the currently selected INSERT (first Block in
 * the selection). No drafting state machine — parses args with the
 * quote-aware tokenizer and dispatches the dynamic/* commands (or prints a
 * selection-guidance message). Also covers PROPERTY <id> <name> <value>,
 * the AI-agent-friendly unified property setter.
 */
export function handleDynamicCommands(ctx: DispatchContext): DispatchResult {
  const {
    draftSession,
    resolvedCommandId,
    upperStart,
    trimmedText,
    selectedEntities,
    geometryDocument,
    dispatch,
    appendCommandFeed,
    t,
    setCommandText,
  } = ctx;

  if (draftSession) return null;

  // Aliases: DYNLIST/DL, DYNSET/DS, DYNRESET/DR (AutoCAD-style, plus
  // short keyboard shortcuts). Russian aliases follow the CIS
  // convention using the full-word command name prefix.
  if (
    resolvedCommandId === "DYNLIST" ||
    upperStart.startsWith("DYNLIST ") ||
    upperStart.startsWith("DL ") ||
    upperStart.startsWith("ДИНСПИС ")
  ) {
    const resolved = requireSelectedDynamicTarget({
      selectedEntities,
      geometryDocument,
      appendCommandFeed,
      t,
      setCommandText,
    });
    if (!resolved) return true;
    const { targetId, entity, blockDef } = resolved;
    const transcript = formatDynListTranscript(entity, blockDef);
    appendCommandFeed(transcript);
    dispatch({
      type: "dynamic/list",
      source: "commandline",
      entityId: targetId,
    });
    setCommandText("");
    return true;
  }
  if (
    resolvedCommandId === "DYNSET" ||
    upperStart.startsWith("DYNSET ") ||
    upperStart.startsWith("DS ") ||
    upperStart.startsWith("ДИНЗАД ")
  ) {
    const resolvedTarget = requireSelectedDynamicTarget({
      selectedEntities,
      geometryDocument,
      appendCommandFeed,
      t,
      setCommandText,
    });
    if (!resolvedTarget) return true;
    // Strip the command token; preserve original casing in the
    // remainder because parameter names and values may be
    // case-sensitive Russian / localized strings.
    const remainder = trimmedText.replace(/^\S+\s*/u, "");
    const tokens = tokenize(remainder);
    if (tokens.length < 2) {
      appendCommandFeed(t("feed.dynset.usage"));
      setCommandText("");
      return true;
    }
    const [paramName, ...valueTokens] = tokens;
    const rawValue = valueTokens.join(" ");
    const { targetId, entity, blockDef } = resolvedTarget;
    // Echo the mutation result to the feed BEFORE dispatch so the
    // user sees the exact error message for unknown parameters,
    // type mismatches, etc. The reducer is a silent no-op on
    // failure (returns the document unchanged); the UI carries
    // the explanation.
    const result = setDynamicParameter(entity, blockDef, paramName ?? "", rawValue);
    if (!result.ok) {
      appendCommandFeed(result.message);
      setCommandText("");
      return true;
    }
    appendCommandFeed(result.summary);
    dispatch({
      type: "dynamic/set",
      source: "commandline",
      entityId: targetId,
      paramName: paramName ?? "",
      rawValue,
    });
    setCommandText("");
    return true;
  }
  if (resolvedCommandId === "DYNRESET") {
    const resolved = requireSelectedDynamicTarget({
      selectedEntities,
      geometryDocument,
      appendCommandFeed,
      t,
      setCommandText,
    });
    if (!resolved) return true;
    const { targetId, entity, blockDef } = resolved;
    const result = resetDynamicParameters(entity, blockDef);
    if (!result.ok) {
      appendCommandFeed(result.message);
      setCommandText("");
      return true;
    }
    appendCommandFeed(result.summary);
    dispatch({
      type: "dynamic/reset",
      source: "commandline",
      entityId: targetId,
    });
    setCommandText("");
    return true;
  }
  // ── PROPERTY <id> <name> <value> — unified property command ─
  // AI-agent-friendly entrypoint: routes to dynamic/set when the
  // name matches a customProperty of a dynamic block, else to
  // chprop/apply for standard properties (Layer, Color, Linetype,
  // Linetype scale, Lineweight, Transparency). See propertyCommand.ts
  // for the pure dispatch planner.
  if (
    resolvedCommandId === "PROPERTY" ||
    upperStart.startsWith("PROPERTY ") ||
    upperStart.startsWith("СВОЙСТВО ")
  ) {
    const remainder = trimmedText.replace(/^\S+\s*/u, "");
    const tokens = tokenize(remainder);
    if (tokens.length < 3) {
      appendCommandFeed(t("prompt.property.usage"));
      setCommandText("");
      return true;
    }
    const [entityId, propertyName, ...valueTokens] = tokens;
    const rawValue = valueTokens.join(" ");
    const plan = planPropertyDispatch(
      {
        entityId: entityId ?? "",
        propertyName: propertyName ?? "",
        rawValue,
      },
      {
        // Round-3 (@codex Finding 4) — cache-aware lookup; see
        // `resolveDynamicBlockTarget`'s doc comment above for why a manual
        // `geometryDocument.spaces[].entities` scan silently failed every
        // valid id in a streamed/very-large document (entities live only
        // in the compact cache there).
        findEntity: (id: string) => getEntityById(id),
      },
    );
    if (plan.kind === "error") {
      const message =
        plan.code === "entity-not-found"
          ? t("error.property.entity_not_found", {
              id: plan.entityId ?? entityId ?? "",
            })
          : plan.code === "unknown-property"
            ? t("error.property.unknown_name", {
                name: plan.propertyName ?? propertyName ?? "",
              })
            : t("error.property.empty_name");
      appendCommandFeed(message);
      setCommandText("");
      return true;
    }
    if (plan.kind === "dynamic-param") {
      // Round-3 (@codex Finding 3) — `planPropertyDispatch` only checks
      // that the entity's `customProperties` bag HAS this key (existence);
      // it does not validate that `rawValue` is a legal value for the
      // parameter's type/constraints (numeric range, lookup-table
      // membership, etc.). `setDynamicParameter` is the single validator
      // for that — the SAME function the DYNSET branch above already
      // calls BEFORE dispatching — and `dynamic/set`'s reducer silently
      // no-ops (0 mutations) on an invalid value. Without this pre-check,
      // PROPERTY reported `audit.property.changed` (success) below
      // unconditionally even when nothing actually changed, exactly the
      // false-success DYNSET was already fixed to avoid.
      const { entity, blockDef } = resolveDynamicBlockTarget(
        geometryDocument,
        plan.entityId,
      );
      const validation = setDynamicParameter(
        entity,
        blockDef,
        plan.paramName,
        plan.rawValue,
      );
      if (!validation.ok) {
        appendCommandFeed(validation.message);
        setCommandText("");
        return true;
      }
      dispatch({
        type: "dynamic/set",
        source: "commandline",
        entityId: plan.entityId,
        paramName: plan.paramName,
        rawValue: plan.rawValue,
      });
    } else {
      dispatch({
        type: "chprop/apply",
        source: "commandline",
        entityIds: [plan.entityId],
        property: plan.storeKey,
        value: plan.rawValue,
      });
    }
    appendCommandFeed(
      t("audit.property.changed", {
        name: plan.kind === "dynamic-param" ? plan.paramName : plan.propertyName,
        id: plan.entityId,
        value: plan.rawValue,
      }),
    );
    setCommandText("");
    return true;
  }

  return null;
}
